/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import Msdyn365, { getRichTextHtml } from '@msdyn365-commerce/core';
import { ProductRefiner, SearchSuggestion } from '@msdyn365-commerce/retail-proxy';

import { format, getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IMfrmSearchData } from '../mfrm-search.data';
import { IMfrmSearchProps } from '../mfrm-search.props.autogenerated';

import { removeKeywords } from '../../../Utilities/plp-url-utils';

export interface ISearchCategorySuggestProps {
    searchCategorySuggestClass: string;
    searchPageURL: string;
    searchQsp: string | undefined;
    hitPrefix: string;
    hitSuffix: string;
    autoSuggestProps: IMfrmSearchProps<IMfrmSearchData>;
    searchText: string;
    searchRefiners: ProductRefiner[] | undefined;
    suggestions: SearchSuggestion[] | undefined;
    telemetryContent?: ITelemetryContent;
}

export interface ISearchCategoryViewProps {
    title: React.ReactNode;
    text: React.ReactNode[];
}

let payLoad: IPayLoad;

/**
 *
 * CategorySuggestionsComponent
 */
export const CategorySuggestionsComponent = (props: ISearchCategorySuggestProps): ISearchCategoryViewProps | undefined => {
    const {
        suggestions,
        searchText,
        autoSuggestProps: {
            resources: { categorySuggestionHeading }
        }
    } = props;

    if (!suggestions) {
        return undefined;
    }

    const reactNodes: React.ReactNode[] = [];
    payLoad = getPayloadObject('click', props.telemetryContent!, '');

    suggestions.map(item => {
        reactNodes.push(_assembleNode(item, props, props.autoSuggestProps));
    });

    const titleNode = _renderTitle(categorySuggestionHeading, searchText);

    return { title: titleNode, text: reactNodes };
};

const _renderTitle = (categorySuggestionHeading: string, searchText: string) => {
    const categoryAutoSuggestionHeading = categorySuggestionHeading
        ? format(categorySuggestionHeading, searchText)
        : `Search "${searchText}" in categories`;

    return <li className='msc-autoSuggest__categoryResults-title'>{categoryAutoSuggestionHeading}</li>;
};

const _assembleNode = (
    item: SearchSuggestion,
    searchProps: ISearchCategorySuggestProps,
    categoryProps: IMfrmSearchProps<IMfrmSearchData>
) => {
    const { hitPrefix, hitSuffix } = searchProps;

    const splitValue = item.Value && item.Value.split('|').pop();
    const ariaLabel = splitValue
        // eslint-disable-next-line security/detect-non-literal-regexp
        ? splitValue?.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '')?.replace(new RegExp(`[${hitSuffix}]`, 'gi'), '')
        // eslint-disable-next-line security/detect-non-literal-regexp
        : item.Value && item.Value?.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '')?.replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');
    payLoad.contentAction.etext = item.Value;
    const attribute = getTelemetryAttributes(searchProps.telemetryContent!, payLoad);
    const linkUrl = _generateLinkPLP(item, categoryProps);
    return (
        <li className='msc-autoSuggest__categoryResults-item' key={item.Id}>
            <a
                aria-label={ariaLabel}
                href={linkUrl}
                {...attribute}
                // tslint:disable-next-line:jsx-no-lambda
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: 'site_search',
                            event_type: 'site_search',
                            search_term: searchProps.searchText,
                            search_type: 'related-categories-search'
                        });
                    }

                    if (Msdyn365.isBrowser && linkUrl) {
                        window.location.href = linkUrl;
                    }
                }}
            >
                {linkUrl && _getHighlightedText(item.Value ? item.Value : '', hitPrefix, hitSuffix)}
            </a>
        </li>
    );
};

const _generateLinkPLP = (suggestion: SearchSuggestion, props: IMfrmSearchProps<IMfrmSearchData>): string | undefined => {
    const categoryPath = suggestion.ExtensionProperties?.find(property => property.Key === 'CategoryPath')?.Value?.StringValue;
    const categoryId = suggestion.Id;

    return suggestion && categoryPath && categoryId ? _getCategoryLinkPLP(categoryPath, categoryId, props) : '';
};

const _getCategoryLinkPLP = (categoryPath: string, categoryId: string, props: IMfrmSearchProps<IMfrmSearchData>): string | undefined => {
    if (Msdyn365.isBrowser) {
        const base = window.location.origin;
        const validCategoryPath = categoryPath
            .toLowerCase()
            ?.replace(/\|/g, '/')
            ?.replace(/&/g, '')
            ?.replace(/ +/g, '-');
        const pathWithRemovedKeyWords = removeKeywords(validCategoryPath.split('/').splice(1), props.context.request).join('/');

        return `${base}/${pathWithRemovedKeyWords}/${categoryId}.c`;
    } else {
        return undefined;
    }
};

const _getHighlightedText = (suggestion: string, hitPrefix: string, hitSuffix: string): JSX.Element => {
    let formattedText = suggestion
        // eslint-disable-next-line security/detect-non-literal-regexp
        ?.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '<strong>')
        // eslint-disable-next-line security/detect-non-literal-regexp
        ?.replace(new RegExp(`[${hitSuffix}]`, 'gi'), '</strong>');
    formattedText = formattedText.split('|').pop() || '';

    // tslint:disable:react-no-dangerous-html
    return <span dangerouslySetInnerHTML={getRichTextHtml(formattedText)} />;
};

/**
 * Compressed a list of refiners for use in URLs
 * @param refiners The uncompressed refiners
 */
